import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {Alert, Stack} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {z} from 'zod';
import type {StudyStatus} from '@cp/model';
import PageHeader from '../../../components/PageHeader';
import {PrivateRouteProps} from '../../../config/routeType';
import {useGenerateStudy} from '../../../hooks/generate/useGenerateStudy';
import useExamModuleTopicSelector from '../../../hooks/helper/useExamModuleTopicSelector';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';
import {useGetStudy} from '../../../hooks/study/useGetStudy';
import {useUpsertStudy} from '../../../hooks/study/useUpsertStudy';
import LoadingPage from '../../other/LoadingPage';
import AdminStudyEditor from './AdminStudyEditor';
import AdminStudyGeneration from './AdminStudyGeneration';
import AdminStudyModule from './AdminStudyModule';
import useAdminStudyPageContentStudyContent from './useAdminStudyPageContentStudyContent';

const studyPageParamsSchema = z.object({
  moduleId: z.string().optional(),
  topic: z.string().optional(),
});

type StudyPageParams = z.infer<typeof studyPageParamsSchema>;

export default function AdminStudyPage(_props: PrivateRouteProps) {
  const navigate = useNavigate();
  const {showMessage} = useMessage();
  const {examModules, selectedModule, selectedTopic, setModuleById, setTopicByTopic, modulesLoading} =
    useExamModuleTopicSelector();

  const {searchParams, setSearchParams} = useSearchParamsHandler<StudyPageParams>({
    schema: studyPageParamsSchema,
    defaultValues: {
      moduleId: '',
      topic: '',
    },
  });

  // Sync URL params to module/topic selector
  useEffect(() => {
    setModuleById(searchParams.moduleId);
    setTopicByTopic(searchParams.topic);
  }, [searchParams.moduleId, searchParams.topic, setModuleById, setTopicByTopic]);

  const {upsertStudy, loading: saving, error: saveError} = useUpsertStudy();
  const {generateStudy, loading: generating, error: generateError} = useGenerateStudy();

  const [additionalInfo, setAdditionalInfo] = useState('');
  const [status, setStatus] = useState<StudyStatus | undefined>(undefined);
  const [showPreview, setShowPreview] = useState(false);

  const {content, setContent, studyContent, setStudyContent, pageMode, setPageMode, getData, setData} =
    useAdminStudyPageContentStudyContent();

  const {
    study,
    loading: studyLoading,
    refetch: refetchStudy,
  } = useGetStudy({
    moduleId: selectedModule?.id || '',
    topic: selectedTopic?.topic || '',
  });

  // Update content and status when study is loaded
  // Combine studyContent array into a single content string
  useEffect(() => {
    setData(study?.studyContent || '');
    setStatus(study?.status || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study]);

  const handleModuleChange = (moduleId?: string) => {
    setSearchParams({
      moduleId: moduleId || '',
      topic: '',
    });
    setData('');
    setAdditionalInfo('');
  };

  const handleTopicChange = (topic?: string) => {
    const topicValue = topic || '';
    // Clear content and status when topic changes (if there was a previous topic)
    if (selectedTopic?.topic && selectedTopic.topic !== topicValue) {
      setData('');
      setStatus(undefined);
    }
    setSearchParams({
      topic: topicValue,
    });
  };

  const handleSave = useCallback(async () => {
    if (!selectedModule?.id || !selectedTopic?.topic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    try {
      const contentToSave = getData();
      await upsertStudy(selectedModule.id, selectedTopic.topic, contentToSave, status);
      showMessage('Study content saved successfully', 6000, 'success');
      // Cache is updated automatically by the mutation, no need to refetch
    } catch (error: any) {
      showMessage(error.message || 'Failed to save study content', 6000, 'error');
    }
  }, [selectedModule?.id, selectedTopic?.topic, getData, status, upsertStudy, showMessage]);

  const handleGenerate = async () => {
    if (!selectedModule?.id || !selectedTopic?.topic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    try {
      const generatedContent = await generateStudy(
        selectedModule.id,
        selectedTopic.topic,
        additionalInfo.trim() || undefined,
      );
      setContent(generatedContent);
      showMessage('Study content generated successfully', 6000, 'success');
      refetchStudy();
    } catch (error: any) {
      showMessage(error.message || 'Failed to generate study content', 6000, 'error');
    }
  };

  const handleViewStudy = () => {
    if (selectedModule?.id && selectedTopic?.topic) {
      navigate(`/course/${selectedModule.id}/study/${encodeURIComponent(selectedTopic.topic)}`);
    }
  };

  if (modulesLoading) {
    return <LoadingPage center minusAppBar />;
  }

  return (
    <ContentContainer data-testid="study-management-page">
      <Stack spacing={3}>
        <PageHeader title="Study Content Management" subtitle="Manage the content of your study" />

        <AdminStudyModule
          examModules={examModules}
          selectedModule={selectedModule}
          selectedTopic={selectedTopic}
          handleModuleChange={handleModuleChange}
          handleTopicChange={handleTopicChange}
        />

        <AdminStudyGeneration
          selectedModule={selectedModule}
          selectedTopic={selectedTopic}
          examModules={examModules}
          modulesLoading={modulesLoading}
          additionalInfo={additionalInfo}
          onAdditionalInfoChange={setAdditionalInfo}
          onContentChange={setData}
          onGenerate={handleGenerate}
          generating={generating}
          saving={saving}
          studyLoading={studyLoading}
        />

        {/* Content Editor */}
        {selectedModule?.id && selectedTopic?.topic && (
          <AdminStudyEditor
            content={content}
            setContent={setContent}
            studyContent={studyContent}
            setStudyContent={setStudyContent}
            pageMode={pageMode}
            setPageMode={setPageMode}
            status={status}
            onStatusChange={setStatus}
            showPreview={showPreview}
            onTogglePreview={() => setShowPreview(!showPreview)}
            studyLoading={studyLoading}
            saving={saving}
            canViewStudy={!!study}
            onViewStudy={handleViewStudy}
            onSave={handleSave}
            moduleId={selectedModule.id}
            topic={selectedTopic.topic}
          />
        )}

        {/* Error Display */}
        {(saveError || generateError) && (
          <Alert severity="error" onClose={() => {}}>
            {saveError?.message || generateError?.message || 'An error occurred'}
          </Alert>
        )}
      </Stack>
    </ContentContainer>
  );
}
